"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { PhotowallArrivalDirection, PhotowallArrivalStyle, PhotowallFlight } from "./usePhotowallArrivals";
import { buildArrivalMotion, type PhotowallArrivalEdge } from "./photowallArrivalMotion";
import styles from "./PhotowallArrivals.module.css";
interface PhotowallArrivalsProps {
  flights: PhotowallFlight[];
  style: Exclude<PhotowallArrivalStyle, "off">;
  direction: PhotowallArrivalDirection;
  qrPosition: string | null;
  isReducedMotion?: boolean;
  isPaused?: boolean;
  onComplete: (id: number) => void;
}
interface StageSize {
  width: number;
  height: number;
  qrExtent: number;
}
interface ArrivalCardProps {
  flight: PhotowallFlight;
  style: Exclude<PhotowallArrivalStyle, "off">;
  direction: PhotowallArrivalDirection;
  edge: PhotowallArrivalEdge;
  stage: StageSize;
  baseCardWidth: number;
  isReducedMotion: boolean;
  isPaused: boolean;
  onComplete: (id: number) => void;
}
function ArrivalCard(t0) {
  const $ = _c(46);
  const {
    flight,
    style,
    direction,
    edge,
    stage,
    baseCardWidth,
    isReducedMotion,
    isPaused,
    onComplete
  } = t0;
  const flightRef = useRef(null);
  const animationRef = useRef(undefined);
  const onCompleteRef = useRef(onComplete);
  let t1;
  if ($[0] !== baseCardWidth || $[1] !== direction || $[2] !== edge || $[3] !== flight.energy || $[4] !== flight.id || $[5] !== flight.sequence || $[6] !== isReducedMotion || $[7] !== stage.height || $[8] !== stage.qrExtent || $[9] !== stage.width || $[10] !== style) {
    t1 = () => buildArrivalMotion({
      style,
      direction,
      edge,
      stageWidth: stage.width,
      stageHeight: stage.height,
      qrExtent: stage.qrExtent,
      baseCardWidth,
      energy: flight.energy,
      seed: flight.id * 31 + flight.sequence,
      isReducedMotion
    });
    $[0] = baseCardWidth;
    $[1] = direction;
    $[2] = edge;
    $[3] = flight.energy;
    $[4] = flight.id;
    $[5] = flight.sequence;
    $[6] = isReducedMotion;
    $[7] = stage.height;
    $[8] = stage.qrExtent;
    $[9] = stage.width;
    $[10] = style;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const [motion] = useState(t1);
  let t2;
  let t3;
  if ($[12] !== onComplete) {
    t2 = () => {
      onCompleteRef.current = onComplete;
    };
    t3 = [onComplete];
    $[12] = onComplete;
    $[13] = t2;
    $[14] = t3;
  } else {
    t2 = $[13];
    t3 = $[14];
  }
  useEffect(t2, t3);
  let t4;
  if ($[15] !== flight.delay || $[16] !== flight.id || $[17] !== motion.duration || $[18] !== motion.easing || $[19] !== motion.keyframes) {
    t4 = () => {
      const element = flightRef.current;
      if (!element) {
        return;
      }
      if (typeof element.animate !== "function") {
        onCompleteRef.current(flight.id);
        return;
      }
      const animation = element.animate(motion.keyframes, {
        duration: motion.duration,
        delay: flight.delay ?? 0,
        easing: motion.easing,
        fill: "both"
      });
      animation.onfinish = () => onCompleteRef.current(flight.id);
      animationRef.current = animation;
      return () => {
        animation.onfinish = null;
        animation.cancel();
        animationRef.current = undefined;
      };
    };
    $[15] = flight.delay;
    $[16] = flight.id;
    $[17] = motion.duration;
    $[18] = motion.easing;
    $[19] = motion.keyframes;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== flight.delay || $[22] !== flight.id || $[23] !== motion) {
    t5 = [motion, flight.id, flight.delay];
    $[21] = flight.delay;
    $[22] = flight.id;
    $[23] = motion;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  useLayoutEffect(t4, t5);
  let t6;
  let t7;
  if ($[25] !== isPaused) {
    t6 = () => {
      const animation_0 = animationRef.current;
      if (!animation_0) {
        return;
      }
      if (isPaused && animation_0.playState === "running") {
        animation_0.pause();
      }
      if (!isPaused && animation_0.playState === "paused") {
        animation_0.play();
      }
    };
    t7 = [isPaused];
    $[25] = isPaused;
    $[26] = t6;
    $[27] = t7;
  } else {
    t6 = $[26];
    t7 = $[27];
  }
  useEffect(t6, t7);
  let t8;
  if ($[28] !== motion.width || $[29] !== motion.zIndex) {
    t8 = {
      width: motion.width,
      zIndex: motion.zIndex
    };
    $[28] = motion.width;
    $[29] = motion.zIndex;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const t9 = flight.id;
  let t10;
  if ($[31] !== flight.energy) {
    t10 = flight.energy.toFixed(2);
    $[31] = flight.energy;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== motion.duration) {
    t11 = Math.round(motion.duration);
    $[33] = motion.duration;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const t12 = `${styles.card} ${styles[style]}`;
  let t13;
  if ($[35] !== flight.url) {
    t13 = <img src={flight.url} alt="" className={styles.image} />;
    $[35] = flight.url;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t12 || $[38] !== t13) {
    t14 = <div className={t12}>{t13}</div>;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== flight.id || $[41] !== t10 || $[42] !== t11 || $[43] !== t14 || $[44] !== t8) {
    t15 = <div ref={flightRef} className={styles.flight} style={t8} data-photowall-flight="" data-image-id={t9} data-energy={t10} data-duration={t11}>{t14}</div>;
    $[40] = flight.id;
    $[41] = t10;
    $[42] = t11;
    $[43] = t14;
    $[44] = t8;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
}
export default function PhotowallArrivals(t0) {
  const $ = _c(22);
  const {
    flights,
    style,
    direction,
    qrPosition,
    isReducedMotion: t1,
    isPaused: t2,
    onComplete
  } = t0;
  const isReducedMotion = t1 === undefined ? false : t1;
  const isPaused = t2 === undefined ? false : t2;
  const stageRef = useRef(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      width: 0,
      height: 0,
      qrExtent: 0
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [stage, setStage] = useState(t3);
  const isVertical = direction === "bottom-to-top" || direction === "top-to-bottom";
  const edge = isVertical ? qrPosition?.startsWith("right") ? "left" : "right" : qrPosition?.endsWith("bottom") ? "top" : "bottom";
  let t4;
  let t5;
  if ($[1] !== edge || $[2] !== qrPosition) {
    t4 = () => {
      const element = stageRef.current;
      if (!element) {
        return;
      }
      const measure = () => {
        const width = element.clientWidth;
        const height = element.clientHeight;
        const qr = qrPosition ? element.parentElement?.querySelector("[data-photowall-qr]") : null;
        let qrExtent = 0;
        if (qr) {
          const left = qr.offsetLeft - element.offsetLeft;
          const top = qr.offsetTop - element.offsetTop;
          if (edge === "right") {
            qrExtent = left + qr.offsetWidth;
          }
          if (edge === "left") {
            qrExtent = width - left;
          }
          if (edge === "top") {
            qrExtent = height - top;
          }
          if (edge === "bottom") {
            qrExtent = top + qr.offsetHeight;
          }
        }
        setStage({
          width,
          height,
          qrExtent: Math.max(0, qrExtent)
        });
      };
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      return () => observer.disconnect();
    };
    t5 = [edge, qrPosition];
    $[1] = edge;
    $[2] = qrPosition;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  const baseCardWidth = Math.max(56, Math.min(170, stage.width * 0.12, stage.height * 0.17));
  let t6;
  if ($[5] !== baseCardWidth || $[6] !== direction || $[7] !== edge || $[8] !== flights || $[9] !== isPaused || $[10] !== isReducedMotion || $[11] !== onComplete || $[12] !== stage || $[13] !== style) {
    t6 = stage.width > 0 && flights.map(flight => <ArrivalCard key={`${flight.id}-${direction}-${edge}-${isReducedMotion}`} flight={flight} style={style} direction={direction} edge={edge} stage={stage} baseCardWidth={baseCardWidth} isReducedMotion={isReducedMotion} isPaused={isPaused} onComplete={onComplete} />);
    $[5] = baseCardWidth;
    $[6] = direction;
    $[7] = edge;
    $[8] = flights;
    $[9] = isPaused;
    $[10] = isReducedMotion;
    $[11] = onComplete;
    $[12] = stage;
    $[13] = style;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== direction || $[16] !== edge || $[17] !== isPaused || $[18] !== isReducedMotion || $[19] !== style || $[20] !== t6) {
    t7 = <div ref={stageRef} className={styles.stage} aria-hidden="true" data-photowall-arrivals="" data-style={style} data-direction={direction} data-edge={edge} data-reduced-motion={isReducedMotion} data-paused={isPaused}>{t6}</div>;
    $[15] = direction;
    $[16] = edge;
    $[17] = isPaused;
    $[18] = isReducedMotion;
    $[19] = style;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}